import { axiosInstance, setAuthToken as setAxiosAuthToken } from '@common/api/axiosInstance';
import {
  LoginResponse,
  AccountError,
  LoginInput,
  loginUser,
  loginUserSession,
  OTPResponse,
  iframeLogin
} from '@common/api/auth';
import { useMutation } from '@tanstack/react-query';
import { isEmpty, isNil } from 'lodash-es';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '@common/store/auth';
import { useNotificationsStore } from '@common/store/notifications';
import { useAppFlags } from '@common/hooks/useAppFlags';
import { ldIdentify } from '@common/helpers/ld-identify';
import { useLDClient } from 'launchdarkly-react-client-sdk';
import { Flags } from '@common/constants/flags';

export const useLoginUser = () => {
  const { t } = useTranslation();
  const addNotification = useNotificationsStore((state) => state.addNotification);
  const { setAuth } = useAuthStore();
  const ldClient = useLDClient();

  return useMutation<LoginResponse, AccountError, LoginInput>({
    mutationFn: loginUser,
    onSuccess: async (data, variables) => {
      console.log('🔑 Initial token login successful', {
        email: data.user.email,
        isStaff: data.user.is_staff,
        hotelCount: data.hotel_list?.length
      });

      // Only For Client Users
      if (isEmpty(data.hotel_list) && !data.user.is_staff && isNil(data.user.sub_account_type)) {
        console.warn('⚠️ User has no hotel access', {
          email: data.user.email,
          isStaff: data.user.is_staff
        });
        addNotification('fail', t('You don`t have permission to access any hotel'));
        return;
      }

      // Set initial token auth
      setAxiosAuthToken(data.token);
      console.log('🔒 Set initial auth token');

      // Identify user in LaunchDarkly to get their specific flags
      if (!ldClient) {
        console.warn('⚠️ LaunchDarkly client not available, defaulting to token auth');
        setAuth(data.token, data.user);
        return;
      }

      await ldIdentify(ldClient, { user: { key: data.user.email, ...data.user } });

      // Get the flag value after identifying the user
      const isSessionBasedAuthEnabled = ldClient.variation(Flags.SessionBasedAuth, false);
      console.log('🚩 LaunchDarkly user identified', {
        email: data.user.email,
        isSessionAuthEnabled: isSessionBasedAuthEnabled
      });

      try {
        if (isSessionBasedAuthEnabled) {
          console.log('🔄 Attempting to convert to session auth');
          try {
            // Convert to session-based auth using the original login input
            const sessionData = await loginUserSession(variables);
            console.log('✅ Session auth conversion successful', {
              headers: axiosInstance.defaults.headers,
              withCredentials: axiosInstance.defaults.withCredentials
            });

            // Clear token to switch to session auth
            setAxiosAuthToken(null, false, sessionData.token);
            setAuth(null, sessionData.user);
            console.log('🔓 Using session auth', {
              headers: axiosInstance.defaults.headers,
              withCredentials: axiosInstance.defaults.withCredentials
            });
          } catch (error) {
            console.error('❌ Session auth conversion failed, falling back to token auth', error);
            setAuth(data.token, data.user);
          }
        } else {
          console.log('🔒 Using token auth (session auth not enabled for user)');
          setAuth(data.token, data.user);
        }

        if (data.user.is_staff) {
          console.log('👤 Staff user detected, sending OTP', {
            headers: axiosInstance.defaults.headers,
            withCredentials: axiosInstance.defaults.withCredentials
          });
          const otpResponse = await axiosInstance.post<OTPResponse>(
            '/account/two-factor-auth/resend_otp/'
          );
          console.log('📨 OTP request response:', otpResponse.status, otpResponse.data);
          addNotification('success', t('Sending OTP Successful'));
        } else {
          console.log('👤 Regular user login completed');
          addNotification('success', t('Login Successful'));
        }
      } catch (error) {
        console.error('❌ Error during auth flow:', error, {
          headers: axiosInstance.defaults.headers,
          withCredentials: axiosInstance.defaults.withCredentials
        });
        throw error;
      }
    },
    onError: (error) => {
      console.error('❌ Login failed', error);
      addNotification('fail', t('Login Failed'));
    }
  });
};

export const useIframeLogin = (
  token: string | null,
  hotel_id: string | null,
  language: string | null
) => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const addNotification = useNotificationsStore((state) => state.addNotification);
  const { setAuth, setHotel } = useAuthStore();

  setAxiosAuthToken(token);

  return useMutation<Omit<LoginResponse, 'token'>, AccountError>({
    mutationFn: iframeLogin,
    onSuccess: async (data) => {
      setAuth(token, data.user);
      i18n.changeLanguage(language ? language : 'en');
      setHotel(+hotel_id!);
      navigate(`/client/${hotel_id}/calendar/pricing`, { replace: true });
      addNotification('success', t('Login Successful'));
    },
    onError: () => {
      addNotification('fail', t('Login Failed'));
    }
  });
};
